import { zodResolver } from '@hookform/resolvers/zod';
import { useMemo, useRef } from 'react';
import { type Resolver, useForm } from 'react-hook-form';
import { earliestScheduleDate } from '@/lib/payments/oneTimePayment.format';
import { makeOneTimePaymentSchema, type OneTimePaymentFormValues } from '@/lib/payments/oneTimePayment.schema';

interface UseOneTimePaymentFormArgs {
  /** From the `maximumPaymentAmount` query. Callers pass a fallback until it resolves. */
  maxAmount: number;
  /** Accounts that cannot be charged, so picking one fails here rather than at the API. */
  expiredAccountIds: string[];
  /** Account id → last ISO date it can be charged, so a date past a card's expiry is caught here. */
  lastChargeableDates: Record<string, string>;
  /** Pre-selected account, normally the member's default. */
  defaultPaymentProfileId: string;
  /** Injectable clock for deterministic date bounds in tests. */
  now?: Date;
}

/**
 * The RHF instance for the one-time payment form.
 *
 * Validation args live in a ref and are read at validation time, so the schema always sees the
 * current maximum and expired-account set without re-creating the form when the queries resolve.
 * Same approach as `usePaymentAccountForm`.
 */
export function useOneTimePaymentForm({
  maxAmount,
  expiredAccountIds,
  lastChargeableDates,
  defaultPaymentProfileId,
  now,
}: UseOneTimePaymentFormArgs) {
  const argsRef = useRef({ maxAmount, expiredAccountIds, lastChargeableDates, now });
  argsRef.current = { maxAmount, expiredAccountIds, lastChargeableDates, now };

  const resolver = useMemo<Resolver<OneTimePaymentFormValues>>(
    () => (values, context, options) => {
      const { maxAmount, expiredAccountIds, lastChargeableDates, now } = argsRef.current;
      const schema = makeOneTimePaymentSchema({ maxAmount, expiredAccountIds, lastChargeableDates, now });
      return (zodResolver(schema) as unknown as Resolver<OneTimePaymentFormValues>)(values, context, options);
    },
    [],
  );

  const defaultValues = useMemo<OneTimePaymentFormValues>(
    () => ({
      amount: '',
      paymentProfileId: defaultPaymentProfileId,
      // The design pre-fills the earliest allowed date rather than leaving the field empty.
      scheduleDate: earliestScheduleDate(now),
    }),
    [defaultPaymentProfileId, now],
  );

  return useForm<OneTimePaymentFormValues>({ resolver, defaultValues, mode: 'onTouched' });
}
